import type { RequestPayload } from '@inertiajs/core'
import { Link, router, usePage } from '@inertiajs/react'
import { ArrowDown, ArrowUp, ExternalLink, Loader2, Plus, Search, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AppLayout from '@/layouts/app-layout'

type FieldValue = string | Array<Record<string, string>>

interface Field {
  value: FieldValue
  label?: string
  type?: 'text' | 'richtext' | 'image' | 'list'
  section?: string
}

interface Version {
  uid: string
  author: string | null
  source: string
  created_at: string
}

interface DeployStatus {
  uid: string
  status: string
  error_message: string | null
  preview_url: string | null
}

interface Props {
  site: { uid: string; name: string }
  data: Record<string, Field>
  schema: Record<string, unknown>
  versions: Version[]
  preview_url: string | null
  latest_deploy: DeployStatus | null
}

const humanize = (s: string) =>
  s.replace(/[_.-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// "Home / Hero" → page "Home", subsection "Hero"; bare sections get their own page.
const pageOf = (field: Field) => (field.section ?? 'Content').split('/')[0].trim()
const subsectionOf = (field: Field) => {
  const parts = (field.section ?? 'Content').split('/')
  return parts.slice(1).join('/').trim() || null
}

export default function Content({ site, data, versions, preview_url, latest_deploy }: Props) {
  const [fields, setFields] = useState<Record<string, Field>>(data)
  const [query, setQuery] = useState('')
  const [deploy, setDeploy] = useState<DeployStatus | null>(latest_deploy)
  const { props } = usePage<{ flash?: { notice?: string } }>()

  const dirtyKeys = useMemo(
    () =>
      Object.keys(fields).filter(
        (k) => JSON.stringify(fields[k]?.value) !== JSON.stringify(data[k]?.value),
      ),
    [fields, data],
  )

  const pages = useMemo(() => {
    const byPage = new Map<string, Array<[string, Field]>>()
    Object.entries(fields).forEach(([key, field]) => {
      const page = pageOf(field)
      if (!byPage.has(page)) byPage.set(page, [])
      byPage.get(page)!.push([key, field])
    })
    return Array.from(byPage.entries())
  }, [fields])

  const [activePage, setActivePage] = useState<string | null>(null)
  const currentPage = activePage ?? pages[0]?.[0] ?? null

  // Search cuts across all pages; otherwise show the active page only.
  const visible = useMemo(() => {
    if (query.trim()) {
      const q = query.toLowerCase()
      return pages
        .map(([page, entries]) =>
          [
            page,
            entries.filter(
              ([key, f]) =>
                key.toLowerCase().includes(q) ||
                (f.label ?? '').toLowerCase().includes(q) ||
                JSON.stringify(f.value).toLowerCase().includes(q),
            ),
          ] as [string, Array<[string, Field]>],
        )
        .filter(([, entries]) => entries.length > 0)
    }
    return pages.filter(([page]) => page === currentPage)
  }, [pages, query, currentPage])

  // Warn before losing unsaved edits on hard navigation.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyKeys.length > 0) e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirtyKeys.length])

  // Poll build status while a rebuild is in flight.
  const building = deploy != null && ['queued', 'building'].includes(deploy.status)
  const pollRef = useRef<number | null>(null)
  useEffect(() => {
    if (!building) return
    pollRef.current = window.setInterval(async () => {
      const res = await fetch(`/sites/${site.uid}/content/status`, {
        headers: { Accept: 'application/json' },
      })
      if (res.ok) setDeploy(await res.json())
    }, 5000)
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current)
    }
  }, [building, site.uid])

  function setValue(key: string, value: FieldValue) {
    setFields((prev) => ({ ...prev, [key]: { ...prev[key], value } }))
  }

  function save() {
    router.post(`/sites/${site.uid}/content`, { data: fields } as unknown as RequestPayload, {
      onSuccess: () => setDeploy((d) => (d ? { ...d, status: 'queued' } : { uid: '', status: 'queued', error_message: null, preview_url: null })),
    })
  }

  function rollback(versionUid: string) {
    if (!window.confirm('Roll back to this version? A rebuild will be queued.')) return
    router.post(`/sites/${site.uid}/content/rollback`, { version_uid: versionUid })
  }

  const previewHref = deploy?.preview_url ?? preview_url

  return (
    <AppLayout
      title={site.name}
      actions={
        <div className="flex items-center gap-3 text-[13px]">
          <Link className="text-primary font-medium hover:underline" href={`/sites/${site.uid}/change_requests`}>
            Change requests
          </Link>
          {previewHref && (
            <Button asChild variant="outline" size="sm">
              <a href={previewHref} target="_blank" rel="noreferrer">
                Open preview
                <ExternalLink className="size-3" />
              </a>
            </Button>
          )}
        </div>
      }
    >
      {props.flash?.notice && !building && (
        <p className="rounded-lg bg-primary/10 px-4 py-2 text-sm text-primary">{props.flash.notice}</p>
      )}
      {building && (
        <p className="flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <Loader2 className="size-4 animate-spin" />
          Rebuilding the site with your changes — the preview link updates when it finishes (~2 min).
        </p>
      )}
      {deploy?.status === 'failed' && (
        <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800">
          The last rebuild failed{deploy.error_message ? `: ${deploy.error_message}` : ''}. Your
          content version is saved — fix or retry from the console, or contact the team.
        </p>
      )}

      {Object.keys(fields).length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No editable content yet — content appears once extraction has run for this site.
        </p>
      ) : (
        <div className="flex min-h-0 flex-1 gap-6">
          {/* Page rail — overflow-hidden keeps the search input from bleeding
              into the fields column and clipping the first label. */}
          <nav className="w-52 shrink-0 overflow-hidden pr-2">
            <div className="relative mb-3">
              <Search className="text-muted-foreground absolute top-2 left-2 size-4" />
              <Input
                className="h-8 pl-8 text-[13px]"
                placeholder="Search fields"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <ul className="space-y-0.5">
              {pages.map(([page, entries]) => (
                <li key={page}>
                  <button
                    className={`w-full rounded-md px-2.5 py-1.5 text-left text-[13px] ${
                      page === currentPage && !query
                        ? 'bg-accent font-semibold'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                    onClick={() => {
                      setQuery('')
                      setActivePage(page)
                    }}
                  >
                    {page}
                    <span className="text-muted-foreground float-right" data-numeric>
                      {entries.length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Fields */}
          <div className="min-w-0 flex-1 overflow-y-auto pb-24">
            {visible.map(([page, entries]) => (
              <section key={page}>
                {query && <h2 className="mt-6 text-[17px] font-semibold first:mt-0">{page}</h2>}
                {groupBySubsection(entries).map(([sub, subEntries]) => (
                  <div key={sub ?? '_'} className="mt-6 first:mt-0">
                    {sub && (
                      <h3 className="border-b pb-2 text-[15px] font-semibold">{sub}</h3>
                    )}
                    <div className="max-w-2xl">
                      {subEntries.map(([key, field]) => (
                        <FieldEditor
                          key={key}
                          fieldKey={key}
                          field={field}
                          dirty={dirtyKeys.includes(key)}
                          onChange={(v) => setValue(key, v)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}

            <details className="mt-12 max-w-2xl">
              <summary className="cursor-pointer text-[15px] font-semibold">
                Version history ({versions.length})
              </summary>
              <ul className="mt-3 space-y-2">
                {versions.map((v, i) => (
                  <li key={v.uid} className="flex items-center gap-3 text-[13px]" data-numeric>
                    <span className="text-muted-foreground w-36 shrink-0">
                      {new Date(v.created_at).toLocaleString()}
                    </span>
                    <span className="truncate">
                      {v.source} · {v.author ?? 'system'}
                    </span>
                    {i === 0 ? (
                      <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-xs">current</span>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => rollback(v.uid)}>
                        Roll back
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      )}

      {/* Sticky save bar */}
      {dirtyKeys.length > 0 && (
        <div className="bg-background fixed right-6 bottom-6 z-20 flex items-center gap-3 rounded-xl border p-3 shadow-lg">
          <span className="text-muted-foreground text-[13px]" data-numeric>
            {dirtyKeys.length} unsaved change{dirtyKeys.length === 1 ? '' : 's'}
          </span>
          <Button variant="outline" size="sm" onClick={() => setFields(data)}>
            Discard
          </Button>
          <Button size="sm" onClick={save} disabled={building}>
            Save &amp; rebuild
          </Button>
        </div>
      )}
    </AppLayout>
  )
}

function groupBySubsection(entries: Array<[string, Field]>) {
  const bySub = new Map<string | null, Array<[string, Field]>>()
  entries.forEach(([key, field]) => {
    const sub = subsectionOf(field)
    if (!bySub.has(sub)) bySub.set(sub, [])
    bySub.get(sub)!.push([key, field])
  })
  return Array.from(bySub.entries())
}

function FieldEditor({
  fieldKey,
  field,
  dirty,
  onChange,
}: {
  fieldKey: string
  field: Field
  dirty: boolean
  onChange: (v: FieldValue) => void
}) {
  return (
    <div className="mt-4">
      <label className="mb-1 flex items-center gap-2 text-[13px] font-semibold" htmlFor={fieldKey}>
        {field.label ?? humanize(fieldKey)}
        {dirty && <span className="bg-amber-400 size-1.5 rounded-full" title="Unsaved change" />}
      </label>
      {field.type === 'richtext' ? (
        <textarea
          id={fieldKey}
          rows={Math.min(10, Math.max(3, String(field.value).split('\n').length + 1))}
          className="border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none"
          value={String(field.value)}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : field.type === 'list' ? (
        <ListEditor items={Array.isArray(field.value) ? field.value : []} onChange={onChange} />
      ) : field.type === 'image' ? (
        <div className="flex items-center gap-3">
          {/^https?:\/\//.test(String(field.value)) && (
            <img
              src={String(field.value)}
              alt=""
              className="size-10 shrink-0 rounded-md border object-cover"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          )}
          <Input id={fieldKey} value={String(field.value)} onChange={(e) => onChange(e.target.value)} />
        </div>
      ) : (
        <Input id={fieldKey} value={String(field.value)} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  )
}

function ListEditor({
  items,
  onChange,
}: {
  items: Array<Record<string, string>>
  onChange: (items: Array<Record<string, string>>) => void
}) {
  const keys = items.length > 0 ? Object.keys(items[0]) : []

  function setItem(index: number, key: string, value: string) {
    onChange(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)))
  }

  function move(index: number, delta: number) {
    const next = [...items]
    const [moved] = next.splice(index, 1)
    next.splice(index + delta, 0, moved)
    onChange(next)
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <fieldset key={index} className="rounded-lg border p-3">
          <div className="text-muted-foreground mb-1 flex items-center justify-between text-xs">
            <span data-numeric>Item {index + 1}</span>
            <span className="flex gap-1">
              <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => move(index, -1)}>
                <ArrowUp className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="size-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onChange(items.filter((_, i) => i !== index))}>
                <Trash2 className="size-3.5" />
              </Button>
            </span>
          </div>
          {keys.map((key) => (
            <div key={key} className="mt-2 first:mt-0">
              <label className="text-muted-foreground text-xs">{humanize(key)}</label>
              {(item[key] ?? '').length > 70 ? (
                <textarea
                  rows={3}
                  className="border-input w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs"
                  value={item[key] ?? ''}
                  onChange={(e) => setItem(index, key, e.target.value)}
                />
              ) : (
                <Input value={item[key] ?? ''} onChange={(e) => setItem(index, key, e.target.value)} />
              )}
            </div>
          ))}
        </fieldset>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...items, Object.fromEntries(keys.map((k) => [k, '']))])}
      >
        <Plus className="size-3.5" />
        Add item
      </Button>
    </div>
  )
}
